import React from 'react';
import { Link, useLocation, useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { NewspaperIcon } from '@heroicons/react/24/outline';

import * as API from '../../api';
import Item from '../../shared/components/PostsCard/Item';

/**
 * Premium /blog listing page — rebuilt on the same Tailwind design system as
 * the redesigned Home page and /events listing (screens/Events/index.js):
 * header band, responsive card grid, skeletons, empty state, and the shared
 * pill Pagination control.
 *
 * Data flow unchanged: API.Posts(page) -> GET /blogs?per_page=9&page=N,
 * returns { data, meta.last_page }.
 */

function CardSkeleton() {
  return (
    <div className="overflow-hidden rounded-2xl border border-slate-100 bg-white shadow-card">
      <div className="aspect-[16/10] animate-pulse bg-slate-100" />
      <div className="space-y-2 p-4">
        <div className="h-3 w-2/5 animate-pulse rounded bg-slate-100" />
        <div className="h-4 w-4/5 animate-pulse rounded bg-slate-100" />
        <div className="h-3 w-full animate-pulse rounded bg-slate-100" />
      </div>
    </div>
  );
}

function Pagination({ page, pages, onChange }) {
  if (pages <= 1) return null;
  const items = [];
  const push = (n) => items.push(n);
  const window = new Set([1, pages, page, page - 1, page + 1]);
  let prev = 0;
  for (let n = 1; n <= pages; n += 1) {
    if (window.has(n)) {
      if (n - prev > 1) push('…');
      push(n);
      prev = n;
    }
  }

  return (
    <nav className="mt-10 flex items-center justify-center gap-1.5" aria-label="Pagination">
      <button
        type="button"
        onClick={() => onChange(Math.max(1, page - 1))}
        disabled={page <= 1}
        className="flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 text-ink transition-colors hover:border-brand-500 hover:text-brand-500 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:border-slate-200 disabled:hover:text-ink"
        aria-label="Previous"
      >
        <svg viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4 rtl:rotate-180" aria-hidden>
          <path fillRule="evenodd" d="M12.79 5.23a.75.75 0 0 1-.02 1.06L8.832 10l3.938 3.71a.75.75 0 1 1-1.04 1.08l-4.5-4.25a.75.75 0 0 1 0-1.08l4.5-4.25a.75.75 0 0 1 1.06.02Z" clipRule="evenodd" />
        </svg>
      </button>

      {items.map((it, i) =>
        it === '…' ? (
          <span key={`e${i}`} className="px-1 text-ink-subtle">
            …
          </span>
        ) : (
          <button
            key={it}
            type="button"
            onClick={() => onChange(it)}
            aria-current={it === page ? 'page' : undefined}
            className={
              it === page
                ? 'flex h-10 w-10 items-center justify-center rounded-full bg-brand-500 text-sm font-semibold text-white shadow-card'
                : 'flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 text-sm font-medium text-ink transition-colors hover:border-brand-500 hover:text-brand-500'
            }
          >
            {it}
          </button>
        )
      )}

      <button
        type="button"
        onClick={() => onChange(Math.min(pages, page + 1))}
        disabled={page >= pages}
        className="flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 text-ink transition-colors hover:border-brand-500 hover:text-brand-500 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:border-slate-200 disabled:hover:text-ink"
        aria-label="Next"
      >
        <svg viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4 rtl:rotate-180" aria-hidden>
          <path fillRule="evenodd" d="M7.21 14.77a.75.75 0 0 1 .02-1.06L11.168 10 7.23 6.29a.75.75 0 1 1 1.04-1.08l4.5 4.25a.75.75 0 0 1 0 1.08l-4.5 4.25a.75.75 0 0 1-1.06-.02Z" clipRule="evenodd" />
        </svg>
      </button>
    </nav>
  );
}

function Blog() {
  const { t } = useTranslation();
  const location = useLocation();
  const history = useHistory();
  const query = new URLSearchParams(location.search);
  const _page = parseInt(query.get('page') || '1', 10);

  const [loadingPosts, setLoadingPosts] = React.useState(false);
  const [posts, setPosts] = React.useState([]);
  const [page, setPage] = React.useState(_page);
  const [pages, setPages] = React.useState(0);

  const loadPosts = (_p) => {
    setLoadingPosts(true);
    API.Posts(_p)
      .then((res) => {
        setPosts(res.data);
        setPages(res.meta.last_page);
      })
      .catch(() => {})
      .then(() => {
        setLoadingPosts(false);
      });
  };

  const handleChangePagination = (value) => {
    setPage(value);
    loadPosts(value);
    // push value of page to the link — same URL contract as before
    history.push(`/blog?page=${value}`);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  React.useEffect(() => {
    loadPosts(page);
    document.title = `${API.APP_NAME} - Blog`;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [0]);

  return (
    <div className="bg-surface-muted">
      {/* ---------- Page header ---------- */}
      <header className="border-b border-slate-100 bg-surface">
        <div className="mx-auto flex w-full max-w-container flex-col items-center px-4 py-10 text-center sm:px-6 sm:py-14 lg:px-8">
          <h1 className="font-display text-3xl font-bold tracking-tight text-ink sm:text-[40px] sm:leading-tight">
            {t('blog.heroTitle')}
          </h1>
          <p className="mt-3 max-w-xl text-sm text-ink-muted sm:text-base">
            {t('blog.heroSubtitle')}
          </p>
        </div>
      </header>

      {/* ---------- Body ---------- */}
      <div className="mx-auto w-full max-w-container px-4 py-8 sm:px-6 lg:px-8 lg:py-12">
        {loadingPosts ? (
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 xl:grid-cols-3">
            {Array.from({ length: 9 }).map((_, i) => (
              <CardSkeleton key={i} />
            ))}
          </div>
        ) : posts.length ? (
          <>
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 xl:grid-cols-3">
              {posts.map((item) => (
                <Link key={item.id} to={`/post/${item.id}?slug=${item.slug}`} className="block h-full">
                  <Item data={item} />
                </Link>
              ))}
            </div>
            <Pagination page={page} pages={pages} onChange={handleChangePagination} />
          </>
        ) : (
          <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-slate-200 bg-white px-6 py-20 text-center">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-surface-muted">
              <NewspaperIcon className="h-7 w-7 text-ink-subtle" />
            </div>
            <h3 className="mt-5 font-display text-lg font-semibold text-ink">
              {t('blog.emptyTitle')}
            </h3>
            <p className="mt-1.5 max-w-sm text-sm text-ink-muted">{t('blog.emptyBody')}</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default Blog;
